import supabase from './supabaseClient';
import { v4 as uuidv4 } from 'uuid';

// ============ CLINICS API ============
export const getClinics = async (filters = {}) => {
  try {
    let query = supabase.from('web_clinics').select('*');

    // Filter by approved status by default
    query = query.eq('status', 'Aprobado');

    if (filters.country) {
      query = query.eq('country', filters.country);
    }
    if (filters.city) {
      query = query.eq('city', filters.city);
    }
    if (filters.specialty) {
      query = query.contains('specialties', [filters.specialty]);
    }

    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching clinics:', error);
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('Error fetching clinics:', error);
    return [];
  }
};

export const getClinic = async (clinicId) => {
  try {
    const { data, error } = await supabase
      .from('web_clinics')
      .select('*')
      .eq('id', clinicId)
      .eq('status', 'Aprobado')
      .single();

    if (error) {
      console.error('Error fetching clinic:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching clinic:', error);
    return null;
  }
};

export const createClinic = async (clinicData) => {
  try {
    // Generate ID
    const clinicId = clinicData.name.toLowerCase().replace(/\s+/g, '-') + '-' + uuidv4().substring(0, 8);

    // Prepare clinic object
    const clinic = {
      id: clinicId,
      name: clinicData.name,
      country: clinicData.country,
      city: clinicData.city,
      phone: clinicData.phone,
      email: clinicData.email || null,
      website: clinicData.website || null,
      address: clinicData.address,
      coordinates: clinicData.coordinates || { lat: 0, lng: 0 },
      specialties: clinicData.specialties,
      image: clinicData.mainImage || '',
      description: clinicData.description,
      about_long: clinicData.aboutLong || null,
      schedule: clinicData.schedule || [],
      gallery: clinicData.galleryImages || [],
      reviews: [],
      branches: [],
      featured: false,
      status: 'Revisar',
    };

    const { data, error } = await supabase
      .from('web_clinics')
      .insert([clinic])
      .select()
      .single();

    if (error) {
      console.error('Error creating clinic:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error creating clinic:', error);
    throw error;
  }
};

// ============ SPECIALTIES API ============
export const getSpecialties = async () => {
  try {
    const { data, error } = await supabase
      .from('web_specialties')
      .select('*');

    if (error) {
      console.error('Error fetching specialties:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching specialties:', error);
    return [];
  }
};

// ============ COUNTRIES API ============
export const getCountries = async () => {
  try {
    const { data, error } = await supabase
      .from('web_countries')
      .select('*');

    if (error) {
      console.error('Error fetching countries:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching countries:', error);
    return [];
  }
};

// ============ CITIES API ============
export const getCities = async () => {
  try {
    const { data, error } = await supabase
      .from('web_cities')
      .select('*');

    if (error) {
      console.error('Error fetching cities:', error);
      return {};
    }

    // Convert to dictionary format { country: [cities] }
    const citiesDict = {};
    (data || []).forEach((doc) => {
      citiesDict[doc.country] = doc.cities;
    });

    return citiesDict;
  } catch (error) {
    console.error('Error fetching cities:', error);
    return {};
  }
};

export const getCitiesByCountry = async (countryCode) => {
  try {
    const { data, error } = await supabase
      .from('web_cities')
      .select('cities')
      .eq('country', countryCode)
      .single();

    if (error) {
      console.error('Error fetching cities by country:', error);
      return [];
    }

    return data?.cities || [];
  } catch (error) {
    console.error('Error fetching cities by country:', error);
    return [];
  }
};

// ============ IMAGE UPLOAD API ============
export const uploadImage = async (file) => {
  try {
    const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3 MB
    const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
    const BUCKET_NAME = 'directorio';

    // Validate file extension
    const fileExt = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExt)) {
      throw new Error(`Tipo de archivo no permitido. Solo se aceptan: ${ALLOWED_EXTENSIONS.join(', ')}`);
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('El archivo excede el tamaño máximo de 3 MB');
    }

    // Generate unique filename
    const uniqueFilename = `${uuidv4()}.${fileExt}`;
    const filePath = `clinics/${uniqueFilename}`;

    // Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(filePath, file);

    if (uploadError) {
      console.error('Error uploading image:', uploadError);
      throw uploadError;
    }

    // Get public URL
    const { data } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(filePath);

    console.log('Image uploaded successfully:', data.publicUrl);

    return {
      success: true,
      url: data.publicUrl,
      filename: uniqueFilename,
    };
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};
